import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Capabilities, type Capability } from '@/api/client'
import { useLogout, useMe } from '@/features/auth/useAuth'
import { useT } from '@/i18n'
import { authMessages } from '@/i18n/messages/auth'
import { commonMessages } from '@/i18n/messages/common'

// The order pages are offered as a landing target — first one the user can reach wins.
const LANDING: { path: string; cap: Capability }[] = [
  { path: '/rtk', cap: Capabilities.ViewRtk },
  { path: '/aufmass', cap: Capabilities.ViewAufmassGrid },
  { path: '/projects', cap: Capabilities.ViewProjects },
  { path: '/bonus', cap: Capabilities.ViewBonus },
  { path: '/users', cap: Capabilities.ManageUsers },
  { path: '/mobile-users', cap: Capabilities.ManageUsers },
]

/** Sends the user to the first page they're allowed to see, or the no-access screen. */
export function Landing() {
  const { data: me } = useMe()
  const first = LANDING.find((p) => me?.capabilities.includes(p.cap))
  return first ? <Navigate to={first.path} replace /> : <NoAccess />
}

/** Gate a route: render it only if the signed-in user holds the capability. */
export function RequireCap({ cap, children }: { cap: Capability; children: ReactNode }) {
  const { data: me } = useMe()
  if (!me) return null
  return me.capabilities.includes(cap) ? <>{children}</> : <NoAccess />
}

/** Gate a route to admins only — the audit Change Log. Matches the server's IsAdmin gate. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { data: me } = useMe()
  if (!me) return null
  return me.isAdmin ? <>{children}</> : <NoAccess />
}

/** Shown when a user is authenticated but has no (or insufficient) grants. */
export function NoAccess() {
  const { data: me } = useMe()
  const logout = useLogout()
  const t = useT(authMessages)
  const tc = useT(commonMessages)
  // The name is bold inside the sentence, so split the sentence around its placeholder.
  const [before, after = ''] = t('noAccessBody').split('{name}')
  return (
    <div className="grid min-h-[60vh] place-items-center px-4">
      <div className="max-w-md rounded-2xl bg-white p-8 text-center ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-amber-100 text-2xl text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
          🔒
        </div>
        <h1 className="text-lg font-semibold">{t('noAccessTitle')}</h1>
        <p className="mt-2 text-sm text-neutral-500">
          {before}<b className="text-neutral-700 dark:text-neutral-200">{me?.name ?? '—'}</b>{after}
        </p>
        <button
          onClick={() => logout.mutate()}
          className="mt-5 rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800"
        >
          {tc('signOut')}
        </button>
      </div>
    </div>
  )
}
